import React, { useEffect } from "react";
import {
  OpTable,
  IOpTableProps,
} from "components/customAntd/DLS/OpTable/OpTable";
import { OpTableRawColumnType } from "components/customAntd/DLS/OpTableCore/OpTableCore";
import { EmailTemplate } from "types/emailTemplateTypes";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import {
  deleteEmailTemplate,
  fetchEmailTemplates,
} from "store/slices/emailTemplatesSlice";
import { notification } from "antd";
import { getRequest } from "api/apiClient";
import { useTranslation } from "react-i18next";
import { hasPermission } from "utils/utils";

interface EmailTemplatesTableProps {
  handleAddClick: () => void;
  handleEditClick: (template: EmailTemplate) => void;
}

const EmailTemplatesTable: React.FC<EmailTemplatesTableProps> = ({
  handleAddClick,
  handleEditClick,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { templates } = useSelector((state: RootState) => state.emailTemplates);
  const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
  const tokenScopeList = useSelector(
    (state: RootState) => state.auth.auth.data[0]?.tokenScopeList || [],
  );
  const fetchEmailTemplatesLoading = useSelector(
    (state: RootState) => state.emailTemplates.fetchEmailTemplatesLoading,
  );
  const hasEmailTemplatesWrite = hasPermission(
    tokenScopeList,
    orgId,
    "o",
    "emailtemplates:w",
  );

  const { t } = useTranslation();

  useEffect(() => {
    if (orgId) {
      dispatch(fetchEmailTemplates({ orgId }));
    }
  }, [dispatch, orgId]);

  const columns: OpTableRawColumnType[] = [
    {
      dataIndex: "name",
      label: t("emailTemplates.table.templateName"),
      filter: {
        type: "input",
      },
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      dataIndex: "description",
      label: t("emailTemplates.table.description"),
      filter: {
        type: "input",
      },
      sorter: (a, b) =>
        (a.description || "").localeCompare(b.description || ""),
    },
  ];

  const handleDeleteClick = async (template: EmailTemplate) => {
    try {
      const response = await getRequest(`/orgs/${orgId}/emailAssignment`);
      const isAssigned = response.data.some(
        (assignment: any) =>
          assignment.emailTemplate?.id === template.id &&
          assignment.emailTemplateType?.name === "Invitation",
      );

      if (isAssigned) {
        notification.error({
          message: t("notification.error"),
          description: t("emailTemplates.notification.deleteErrorCanNotDelete"),
          placement: "bottomRight",
        });
        return;
      }
      await dispatch(deleteEmailTemplate({ orgId, templateId: template.id }));
      notification.success({
        message: t("notification.success"),
        description: t("emailTemplates.notification.deleteSuccess"),
        placement: "bottomRight",
      });
      await dispatch(fetchEmailTemplates({ orgId }));
    } catch (error) {
      notification.error({
        message: t("notification.error"),
        description: t("emailTemplates.notification.deleteError"),
        placement: "bottomRight",
      });
    }
  };

  const opTableProps: IOpTableProps = {
    columns,
    dataSource: templates,
    rowActions: {
      onEditClick: handleEditClick,
      onDeleteClick: hasEmailTemplatesWrite ? handleDeleteClick : undefined,
      deleteModalTitle: () =>
        t("confirmDelete.title", { title: t("emailTemplates.itemName") }),
      deleteModalContent: ({ record }) =>
        t("confirmDelete.message", { name: record.name }),
    },
    writeAccess: hasEmailTemplatesWrite,
    allowAddition: hasEmailTemplatesWrite
      ? {
          itemName: t("emailTemplates.itemName"),
          onClick: handleAddClick,
        }
      : false,
    loading: fetchEmailTemplatesLoading,
  };

  return <OpTable {...opTableProps} />;
};

export default EmailTemplatesTable;
